import { useCallback } from 'react'
import {
  Lab,
  Panel,
  PanelItem,
  PanelItemUnit,
} from '../../../../types/bloodWork'
import randomId from '../../../../utils/randomId'

interface LabEditorActions {
  updatePanel: (panelId: string, partial: Partial<Panel>) => void
  addPanelItem: (panelId: string) => void
  archivePanel: (panelId: string) => void
  toggleProvocationType: (panelId: string) => void
  updateItem: (
    panelId: string,
    itemId: string,
    partial: Partial<PanelItem>
  ) => void
  updateItemUnit: (
    panelId: string,
    itemId: string,
    unitId: string,
    partial: Partial<PanelItemUnit>
  ) => void
  addItemUnit: (panelId: string, itemId: string) => void
  removeItemUnit: (panelId: string, itemId: string, unitId: string) => void
}

export default function useLabEditor(
  setLab: React.Dispatch<React.SetStateAction<Lab>>
): LabEditorActions {
  const updatePanel = useCallback(
    (panelId: string, partial: Partial<Panel>) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId ? { ...p, ...partial } : p
        ),
      }))
    },
    [setLab]
  )

  const addPanelItem = useCallback(
    (panelId: string) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                items: [
                  ...p.items,
                  {
                    id: randomId(),
                    referenceRange: {
                      bottom: '',
                      top: '',
                    },
                    optimalRange: {
                      bottom: '',
                      top: '',
                    },
                    title: '',
                    unit: '',
                    units: [
                      {
                        id: '',
                        referenceRange: {
                          bottom: '',
                          top: '',
                        },
                        optimalRange: {
                          bottom: '',
                          top: '',
                        },
                      },
                    ],
                    archived: false,
                  },
                ],
              }
            : p
        ),
      }))
    },
    [setLab]
  )

  const archivePanel = useCallback(
    (panelId: string) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId ? { ...p, archived: !p.archived } : p
        ),
      }))
    },
    [setLab]
  )

  const toggleProvocationType = useCallback(
    (panelId: string) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? { ...p, requiresProvocationType: !p.requiresProvocationType }
            : p
        ),
      }))
    },
    [setLab]
  )

  const updateItem = useCallback(
    (panelId: string, itemId: string, partial: Partial<PanelItem>) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                items: p.items.map((item) =>
                  item.id === itemId ? { ...item, ...partial } : item
                ),
              }
            : p
        ),
      }))
    },
    [setLab]
  )

  const updateItemUnit = useCallback(
    (
      panelId: string,
      itemId: string,
      unitId: string,
      partial: Partial<PanelItemUnit>
    ) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                items: p.items.map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        units: item.units?.map((unit) =>
                          unit.id === unitId ? { ...unit, ...partial } : unit
                        ),
                      }
                    : item
                ),
              }
            : p
        ),
      }))
    },
    [setLab]
  )

  const addItemUnit = useCallback(
    (panelId: string, itemId: string) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                items: p.items.map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        units: [
                          ...(item.units ?? []),
                          {
                            id: '',
                            referenceRange: {
                              bottom: '',
                              top: '',
                            },
                            optimalRange: {
                              bottom: '',
                              top: '',
                            },
                          },
                        ],
                      }
                    : item
                ),
              }
            : p
        ),
      }))
    },
    [setLab]
  )

  const removeItemUnit = useCallback(
    (panelId: string, itemId: string, unitId: string) => {
      setLab((prev) => ({
        ...prev,
        panels: prev.panels.map((p) =>
          p.id === panelId
            ? {
                ...p,
                items: p.items.map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        units: item.units?.filter((u) => u.id !== unitId),
                      }
                    : item
                ),
              }
            : p
        ),
      }))
    },
    [setLab]
  )

  return {
    updatePanel,
    addPanelItem,
    archivePanel,
    toggleProvocationType,
    updateItem,
    updateItemUnit,
    addItemUnit,
    removeItemUnit,
  }
}
